import type { z } from 'zod';

import { type ColumnType, numberCol, stringCol, unknownCol } from '#core/lib/expr/schema/types.ts';
import { InstanceIdType, type TimeSeriesRefType } from '#core/schema/defs.ts';

export type DistributiveOmit<T, KeyType extends PropertyKey> = T extends unknown ? Omit<T, KeyType> : never;
export type Overwrite<T, OverrideType> = DistributiveOmit<T, keyof OverrideType> & OverrideType;

export type ComponentSchema = z.ZodObject<{ type: z.ZodLiteral<string> }>;
export type ComponentType = z.infer<ComponentSchema>;

export type SchemaComponentType = {
  $$schema?: ComponentSchema;
};

export function isInstanceReference(value: unknown): value is InstanceIdType {
  return (
    typeof value === 'object' &&
    value !== null &&
    'space' in value &&
    'externalId' in value &&
    typeof value.space === 'string' &&
    typeof value.externalId === 'string'
  );
}

/**
 * Type guard to check if a value is a strict InstanceReference.
 * Strict: rejects objects with extra properties.
 *
 * @param value - The value to check
 * @returns True if the value is exactly { space: string, externalId: string }
 */
export function isStrictInstanceReference(value: unknown): value is InstanceIdType {
  return InstanceIdType.safeParse(value).success;
}
/**
 * Canonical string key for a CDF node identity.
 *
 * Single source of truth for the `{ space, externalId }` → string encoding
 * (dash-joined). Use this anywhere a node identity must be flattened into a
 * stable string id or dedup key, instead of re-formatting the template inline.
 */
export function instanceKey({ space, externalId }: InstanceIdType): string {
  return `${space}-${externalId}`;
}

/**
 * The unwrapped Zod object shape of a single view property — the
 * `Record<fieldName, fieldSchema>` produced after `deepRemoveDefaults`
 * walks a direct-relation field down to its referenced object. Each
 * entry exposes the Zod v4 `type` tag (`'boolean'`, `'enum'`, …) and,
 * for the `__typename` literal, its `value`. This is the structural
 * surface the TimeSeries-ref predicates inspect — never the `__typename`
 * STRING, which is display-only.
 */
export type ViewRefShape =
  | Record<string, { type?: string; value?: string; options?: readonly unknown[] } | undefined>
  | undefined;

/**
 * True for the LEGACY CDF `TimeSeries` family (`__typename: 'TimeSeries'`):
 * a flat reference whose object shape carries `externalId` + `id` and
 * NO `type` field. Distinct from the CDM instance family, which is
 * detected structurally by {@link isTimeSeriesRef}.
 *
 * `__typename === 'TimeSeries'` is the discriminator here BECAUSE the
 * legacy shape has no other distinguishing structural marker (it is just
 * `{ externalId, id }`); the literal value is reliable for legacy since
 * it is the one and only emitted `__typename` for that family.
 */
export const isLegacyTimeSeriesRef = (shape: ViewRefShape): boolean =>
  shape?.__typename?.value === 'TimeSeries' &&
  shape?.externalId !== undefined &&
  shape?.id !== undefined &&
  shape?.type === undefined;

/**
 * Single source of truth for "this view property is a TimeSeries
 * reference" — the predicate that makes a column a `nested-dataset`
 * (TS-ref) column.
 *
 * Consumed by `zodFieldToColumnType` (the schema side) AND, by
 * extension, by `projectCell` (the cell side, which only enters its
 * TS-ref branch when the schema already says `nested-dataset`). Keeping
 * the decision here means the two sides can never re-derive it
 * independently and drift — the divergence that produced
 * `liftPerCell`'s "nested-dataset column invariant violated" throw.
 *
 * Detection is STRUCTURAL, not name-based: the CDF codegen emits many
 * distinct `__typename`s that are all TimeSeries projections of two
 * underlying families, so matching the `__typename` string is wrong
 * (`=== 'TimeSeries'` misses every CDM projection; `endsWith('TimeSeries')`
 * both false-negatives projections that don't end in "TimeSeries" — e.g.
 * a schema whose `__typename` is dropped entirely — and false-positives
 * unrelated views).
 *
 *   - CDM instance family (`CogniteTimeSeries`, `PanoramaTimeSeries`,
 *     `LiftingTimeSeries`, `Refinery*TimeSeries`, …): every projection's
 *     shape carries `isStep` (boolean) AND `type` (the
 *     `CogniteTimeSeriesType` enum). `isStep` is the load-bearing
 *     discriminator — no non-TimeSeries object schema carries it — while
 *     `type`-being-an-enum guards against the rare non-TS schema that
 *     happens to expose an `isStep`-like field.
 *   - Legacy `TimeSeries` (`{ externalId, id }`): see
 *     {@link isLegacyTimeSeriesRef}.
 *
 * The `__typename` string remains available on the descriptor for
 * DISPLAY (e.g. rendering `timeseries<number><CogniteTimeSeries>`); it is just no longer
 * the basis for this boolean classification.
 */
export const isTimeSeriesRef = (shape: ViewRefShape): boolean =>
  (shape?.isStep?.type === 'boolean' && shape?.type?.type === 'enum') || isLegacyTimeSeriesRef(shape);
/**
 * The engine `value` {@link ColumnType} a TimeSeries reference carries — the
 * inner datapoint value type its `nested-dataset` cells project. Read STRUCTURALLY
 * from the CDM `CogniteTimeSeriesType` discriminator on the ref shape's `type`
 * field (the codegen enum whose only string-valued member is `'string'`):
 *
 *   - a discriminator constrained to the string series yields {@link stringCol};
 *   - a discriminator present but numeric/mixed yields {@link numberCol};
 *   - a ref with NO discriminator at all (the `TimeSeries` family carries only
 *     `externalId` + `id`) has a genuinely unknown value type at schema time, so
 *     it yields {@link unknownCol} — deferring resolution to fetch, where the
 *     authoritative per-series `isString` flag decides string vs numeric. This
 *     is what stops a string-valued legacy series being mis-declared numeric and
 *     coerced to `NaN` at the runtime boundary.
 *
 * SSOT for "TimeSeries ref → datapoint value type", consumed by the schema side
 * (`zodFieldToColumnType` → `tsRefInnerSchema`) so a string-valued series types
 * its `value` column as `string` rather than the historical hardcoded `number`.
 */
export const tsRefValueType = (shape: ViewRefShape): ColumnType => {
  const options = shape?.type?.options;
  if (!Array.isArray(options) || options.length === 0) {
    return unknownCol;
  }
  return options.every((o: unknown) => o === 'string') ? stringCol : numberCol;
};

/**
 * Extract a CogniteTimeSeries reference from a raw CDF property value,
 * accepting BOTH encodings an instance query can return:
 *
 *   - a bare externalId string
 *     (`"DJ0078-L:WellOutputs:priorityOilRateStd"`) — `space` omitted in
 *     the result (CDF didn't carry one).
 *   - a `{ space?, externalId }` instance-reference object — `space`
 *     preserved in the result when present.
 *
 * Returns `null` for genuinely absent / malformed values (null,
 * undefined, empty string, missing or non-string externalId). The
 * single source of truth for "raw CDF value → ts ref", used by
 * `projectCell`. Pair with {@link tsRefCell} to build the corresponding
 * lazy cell.
 */
export const extractTsRef = (value: unknown): TimeSeriesRefType | null => {
  if (typeof value === 'string') {
    return value.length > 0 ? { externalId: value } : null;
  }
  if (value !== null && typeof value === 'object' && 'externalId' in value) {
    const { externalId, space } = value as { externalId: unknown; space?: unknown };
    if (typeof externalId !== 'string' || externalId.length === 0) {
      return null;
    }
    return typeof space === 'string' && space.length > 0 ? { space, externalId } : { externalId };
  }
  return null;
};
